import React, { useMemo, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { type ColDef } from 'ag-grid-community'
import { GET_ASSET_SUM_UNWIND, GET_ASSETS_WITH_FR_ID_UNWIND } from '../../../components/graphql/queries'
import { useQueryWithExtendedTime } from '../../../hooks/useQueryWithExtendedTime'
import AvatarProfile from '../../../components/avatar'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { useUserEmail } from '../../../hooks/useUserEmail'
import { formatInputToDollar } from '../../../utility/formatting'

const UnwindPage: React.FC = () => {
  const [selectedFundingRequestId, setSelectedFundingRequestId] = useState<number | null>(null)
  const [selectedFundingRequestCode, setSelectedFundingRequestCode] = useState<string | null>(null)
  const { userEmail } = useUserEmail()

  const { data, loading, error, QueryWithExtendedTimeComponent } = useQueryWithExtendedTime({
    query: GET_ASSET_SUM_UNWIND,
    options: {
      variables: { returnResultOption: 'availUnwind', running_user: userEmail }
    }
  })

  const { data: assetDetailsData, loading: assetLoading, error: assetError } = useQueryWithExtendedTime({
    query: GET_ASSETS_WITH_FR_ID_UNWIND,
    options: {
      variables: {
        fundingRequestId: selectedFundingRequestId,
        data_purpose: 'UI-UNWIND'
      },
      skip: !selectedFundingRequestId
    }
  })

  const rowData = useMemo(() => {
    if (!data?.getRawAssetSumByOption) return []
    return data.getRawAssetSumByOption.map((item: any) => ({
      ...item,
      ScheduledTerminationDate: item.ScheduledTerminationDate ? item.ScheduledTerminationDate.split('T')[0] : ''
    }))
  }, [data])

  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Term',
      field: 'Term',
      minWidth: 80,
      maxWidth: 100,
      headerTooltip: 'Term',
      tooltipField: 'Term',
      resizable: true
    },
    {
      headerName: 'Scheduled Termination Date',
      field: 'ScheduledTerminationDate',
      flex: 1,
      cellStyle: { textAlign: 'right' },
      headerTooltip: 'Scheduled Termination Date',
      tooltipField: 'ScheduledTerminationDate',
      resizable: true
    },
    { headerName: 'Status', field: 'StatusFRAssetsUnwind', minWidth: 100, maxWidth: 150, headerTooltip: 'Status', tooltipField: 'StatusFRAssetsUnwind', resizable: true },
    { headerName: 'Funding Request', field: 'FundingRequestCode', minWidth: 100, maxWidth: 170, headerTooltip: 'Funding Request Code', tooltipField: 'FundingRequestCode', resizable: true },
    { headerName: 'Quantity', field: 'Quantity', minWidth: 100, maxWidth: 140, cellStyle: { textAlign: 'right' }, headerTooltip: 'Quantity', tooltipField: 'Quantity', resizable: true, valueFormatter: params => formatInputToDollar(params.value, { digit: 0 }) },
    { headerName: 'Cost Notional', field: 'CostNotional', flex: 1, cellStyle: { textAlign: 'right' }, headerTooltip: 'Cost Notional', tooltipField: 'CostNotional', resizable: true },
    { headerName: 'Total Return Receiver', field: 'TotalReturnReceiver', flex: 1, headerTooltip: 'Total Return Receiver', tooltipField: 'TotalReturnReceiver', resizable: true },
    { headerName: 'TRS Financing Rate', field: 'TRSFinancingRateValFullyConverted', flex: 1, cellStyle: { textAlign: 'right' }, headerTooltip: 'TRS Financing Rate (Fully Converted)', tooltipField: 'TRSFinancingRateValFullyConverted', resizable: true }
  ], [])

  const onRowClicked = (event: any) => {
    const fundingRequestId = event.data.FundingRequestId
    const fundingRequestCode = event.data.FundingRequestCode
    if (fundingRequestId) {
      setSelectedFundingRequestId(fundingRequestId)
      setSelectedFundingRequestCode(fundingRequestCode)
    }
  }

  const onFilterChanged = (params: any) => {
    const displayedNodes = params.api.getRenderedNodes()
      .filter((node: any) => node.displayed)

    if (displayedNodes.length > 0) {
      const currentlySelectedNode = displayedNodes.find((node: any) =>
        node.data && !node.group && node.data.FundingRequestId === selectedFundingRequestId
      )

      let nodeToSelect = null

      if (currentlySelectedNode) {
        nodeToSelect = currentlySelectedNode
      } else {
        nodeToSelect = displayedNodes.find((node: any) =>
          node.data && !node.group
        )
      }

      if (nodeToSelect) {
        const fundingRequestId = nodeToSelect.data.FundingRequestId
        const fundingRequestCode = nodeToSelect.data.FundingRequestCode
        if (fundingRequestId) {
          setSelectedFundingRequestId(fundingRequestId)
          setSelectedFundingRequestCode(fundingRequestCode)
        }

        params.api.deselectAll()
        nodeToSelect.setSelected(true)
        params.api.ensureIndexVisible(nodeToSelect.rowIndex)
      }
    }
  }

  const assetDetailsColumnDefs: ColDef[] = useMemo(() => [
    { headerName: 'Name', field: 'Name', flex: 2, headerTooltip: 'Name', tooltipField: 'Name', minWidth: 125 },
    { headerName: 'Ticker', field: 'Ticker', flex: 0.8, headerTooltip: 'Ticker', tooltipField: 'Ticker', minWidth: 100 },
    { headerName: 'Quantity', field: 'Quantity', flex: 1.2, cellStyle: { textAlign: 'right' }, headerTooltip: 'Quantity', tooltipField: 'Quantity', valueFormatter: params => formatInputToDollar(params.value, { digit: 0 }), minWidth: 115 },
    { headerName: 'Side', field: 'BuyOrSell', flex: 0.8, headerTooltip: 'Side', tooltipField: 'BuyOrSell', minWidth: 90 },
    { headerName: 'Price', field: 'Price', flex: 1.0, cellStyle: { textAlign: 'right' }, headerTooltip: 'Price', tooltipField: 'Price', minWidth: 92 },
    { headerName: 'Cost Notional', field: 'CostNotional', flex: 1.5, cellStyle: { textAlign: 'right' }, headerTooltip: 'Cost Notional', tooltipField: 'CostNotional', minWidth: 120 },
    { headerName: 'Market Value', field: 'MarketValue', flex: 1.5, cellStyle: { textAlign: 'right' }, headerTooltip: 'Market Value', tooltipField: 'MarketValue', minWidth: 120 },
    { headerName: 'Isin', field: 'Isin', flex: 1.2, headerTooltip: 'Isin', tooltipField: 'Isin', minWidth: 120 },
    { headerName: 'Currency', field: 'Currency', flex: 1, headerTooltip: 'Currency', tooltipField: 'Currency', minWidth: 113 },
    { headerName: 'Country', field: 'Country', flex: 1, headerTooltip: 'Country', tooltipField: 'Country', minWidth: 108 }
  ], [])

  const assetDetailsRows = assetDetailsData?.getFundingRequestAssociatedAssetDetailsById?.detailsData || []

  const marketValueTotal = assetDetailsData?.getFundingRequestAssociatedAssetDetailsById?.summaryData.assetTradeDetails.MarketValue || ''
  const quantityTotal = assetDetailsData?.getFundingRequestAssociatedAssetDetailsById?.summaryData.assetTradeDetails.Quantity || ''
  const costNotionalTotal = assetDetailsData?.getFundingRequestAssociatedAssetDetailsById?.summaryData.assetTradeDetails.CostNotional || ''
  const pinnedBottomRowData = [
    {
      Name: 'Total',
      Ticker: '',
      Quantity: quantityTotal,
      BuyOrSell: '',
      Price: '',
      MarketValue: marketValueTotal,
      CostNotional: costNotionalTotal,
      Isin: '',
      Currency: '',
      Country: ''
    }
  ]

  if (loading || error) return <QueryWithExtendedTimeComponent />

  return (
    <div className="w-screen">
      <AvatarProfile />
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-200px)]">
          <div className="md:w-1/2 w-full h-full">
          <h2 className="text-2xl font-bold mb-4">TRS Maturity</h2>
            <div className="ag-theme-alpine nwc-compact w-full h-full">
              <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 80
                }}
                animateRows={true}
                rowSelection="single"
                domLayout="normal"
                headerHeight={60}
                rowHeight={50}
                groupDisplayType="multipleColumns"
                rowGroupPanelShow="always"
                groupDefaultExpanded={1}
                suppressAggFuncInHeader={true}
                autoGroupColumnDef={{
                  headerName: 'Group',
                  cellRendererParams: {
                    suppressCount: false,
                    suppressDoubleClickExpand: false
                  },
                  resizable: true
                }}
                onGridReady={params => { params.api.sizeColumnsToFit() }}
                onRowClicked={onRowClicked}
                onFilterChanged={onFilterChanged}
                enableBrowserTooltips={true}
              />
            </div>
          </div>
          <div className="md:w-1/2 w-full h-full">
            {assetLoading && (
              <div className="mt-4 text-center">
                <p>Loading asset details...</p>
              </div>
            )}
            {assetError && (
              <div className="mt-4 text-center text-red-600">
                <p>Error loading asset details: {assetError.message}</p>
              </div>
            )}
            {assetDetailsRows.length > 0 && (
              <div className="h-full">
                <h2 className="text-2xl font-bold mb-4">Asset Details for Funding Request: {selectedFundingRequestCode}</h2>
                <div className="ag-theme-alpine nwc-compact w-full h-[calc(100%)]">
                  <AgGridReact
                    columnDefs={assetDetailsColumnDefs}
                    rowData={assetDetailsRows}
                    defaultColDef={{
                      sortable: true,
                      filter: true,
                      resizable: true
                    }}
                    domLayout="normal"
                    headerHeight={60}
                    rowHeight={50}
                    enableBrowserTooltips={true}
                    pinnedBottomRowData={pinnedBottomRowData}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UnwindPage
